import { FormControlLabel, Switch } from '@material-ui/core'
import * as Sentry from '@sentry/react'
import { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { Container } from 'react-bootstrap'
import Helmet from 'react-helmet'
import { useSelector } from 'react-redux'
import { getAppConfig } from 'studiokit-scaffolding-js'
import { ErrorBoundary } from 'studiokit-scaffolding-js/lib/components/ErrorBoundary'
import { Footer } from 'studiokit-scaffolding-js/lib/components/Footer'
import { Loading } from 'studiokit-scaffolding-js/lib/components/Loading'
import { persistenceService } from 'studiokit-scaffolding-js/lib/services/persistenceService'
import CONFIG from '../configuration'
import { useClientConfig } from '../hooks/useClientConfig'
import { useTokenUsage } from '../hooks/useTokenUsage'
import { useUserInfo } from '../hooks/useUserInfo'
import { ReduxState } from '../types/ReduxState'
import { logIn } from '../utils/authentication'
import { AppContext, AppContextType } from './AppContext'
import { Header } from './Header'
import WrappedRoutes from './Routes'

export const App: FunctionComponent = () => {
	const isAppInitialized = useSelector((state: ReduxState) => CONFIG.IS_DOWNTIME || state.auth.isInitialized)

	//#region Authentication

	const isAuthenticated = useSelector((state: ReduxState) => state.auth.isAuthenticated)
	const isAuthenticating = useSelector((state: ReduxState) => state.auth.isAuthenticating)
	const appConfig = getAppConfig()
	const oauthToken = persistenceService.getPersistedToken()

	// automatically login on mount, if not currently authenticating and either not authenticated or if required scope has increased
	useEffect(() => {
		if (
			!isAuthenticating &&
			(!isAuthenticated ||
				(oauthToken &&
					appConfig.OAUTH_PKCE_SCOPE &&
					appConfig.OAUTH_PKCE_SCOPE.split(' ').some(s => !oauthToken.scope?.includes(s))))
		) {
			logIn()
		}
		// only trigger on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	//#endregion Authentication

	//#region App Data

	const clientConfig = useClientConfig(isAuthenticated)
	const { userInfo, userInfoStatus } = useUserInfo(isAuthenticated)
	// a user who is not allowed to convert anything cannot spend tokens, so do not track usage for them
	const { usage, refreshUsage } = useTokenUsage(isAuthenticated && !!userInfo?.hasAccess)

	//#endregion App Data

	const [showMockUI, setShowMockUI] = useState<boolean>(appConfig.NODE_ENV === 'development')
	const appContextValue: AppContextType = useMemo(
		() => ({ showMockUI, clientConfig, usage, refreshUsage, userInfo, userInfoStatus }),
		[showMockUI, clientConfig, usage, refreshUsage, userInfo, userInfoStatus]
	)

	return (
		<>
			<Helmet
				defaultTitle="ink2html"
				titleTemplate="ink2html - %s"
				meta={[
					{ name: 'description', content: 'ink2html - Purdue' },
					{ property: 'og:type', content: 'article' }
				]}
				link={[]}
			/>
			<a className="skip-main" href="#main">
				Skip to content
			</a>
			<ErrorBoundary>
				{/* the provider wraps `Header` too, so it can show the usage meter */}
				<AppContext.Provider value={appContextValue}>
					<Container fluid className="flex flex-column pa0">
						<Header isAuthenticated={isAuthenticated} />
						<main id="main" className="flex flex-auto">
							{!isAppInitialized ? <Loading /> : <WrappedRoutes />}
						</main>
						<Footer useDarkBackground />
						{appConfig.NODE_ENV === 'development' && (
							<div className="br-pill bg-color-white shadow-sm absolute" style={{ right: 500, top: 15 }}>
								<FormControlLabel
									label={<span>Show mock UI</span>}
									labelPlacement="start"
									className="ma0 pl3"
									control={
										<Switch
											color="primary"
											checked={showMockUI}
											onChange={() => setShowMockUI(!showMockUI)}
										/>
									}
								/>
							</div>
						)}
					</Container>
				</AppContext.Provider>
			</ErrorBoundary>
		</>
	)
}

const WrappedApp = Sentry.withProfiler(App)
export default WrappedApp
